/**
 * Real Hospital/Branch selection (STEP 4 "Switch Branch") — wires
 * `store/hospital.store.ts`/`store/branch.store.ts` (F0's own
 * deliberately-left-unpopulated "current selection" slots, per their
 * own header comments) to the real `GET /hospitals`/`GET /branches`
 * endpoints for the first time. Opened from `HospitalBranchSwitcher`
 * (header) and from the Quick Actions widget/Quick Search.
 */
import { useState, type JSX } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import { AppDialog as Dialog } from '@components/AppDialog';
import { useQuery } from '@tanstack/react-query';
import { listHospitals, listBranches } from '@services/platform.service';
import { dashboardQueryKeys } from '../queryKeys';
import { useHospitalStore } from '@store/hospital.store';
import { useBranchStore } from '@store/branch.store';

export interface SwitchBranchDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SwitchBranchDialog({ open, onClose }: SwitchBranchDialogProps): JSX.Element {
  const currentHospital = useHospitalStore((state) => state.currentHospital);
  const setHospital = useHospitalStore((state) => state.setHospital);
  const setBranch = useBranchStore((state) => state.setBranch);

  const [hospitalId, setHospitalId] = useState<string>(currentHospital?.id ?? '');
  const [branchId, setBranchId] = useState<string>('');

  const { data: hospitals = [], isLoading: hospitalsLoading } = useQuery({
    queryKey: dashboardQueryKeys.hospitals(),
    queryFn: listHospitals,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: dashboardQueryKeys.branches(hospitalId),
    queryFn: () => listBranches(hospitalId),
    enabled: open && Boolean(hospitalId),
    staleTime: 5 * 60_000,
  });

  function handleConfirm(): void {
    const hospital = hospitals.find((entry) => entry.id === hospitalId);
    const branch = branches.find((entry) => entry.id === branchId);
    if (hospital) setHospital({ id: hospital.id, legalName: hospital.legalName, displayName: hospital.displayName });
    setBranch(branch ? { id: branch.id, code: branch.code, name: branch.name } : null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Switch Hospital / Branch</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {hospitals.length === 0 && !hospitalsLoading && <Alert severity="info">No hospitals are configured for this tenant yet.</Alert>}
          <TextField
            select
            fullWidth
            label="Hospital"
            value={hospitalId}
            onChange={(event) => {
              setHospitalId(event.target.value);
              setBranchId('');
            }}
            disabled={hospitalsLoading}
          >
            {hospitals.map((hospital) => (
              <MenuItem key={hospital.id} value={hospital.id}>
                {hospital.displayName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Branch"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            disabled={!hospitalId || branchesLoading}
            helperText={!hospitalId ? 'Select a hospital first' : undefined}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!hospitalId}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
